import { useState, useCallback } from "react";
import { validateField, validateAll, getDefaultValues } from "../validation/form.validation";
import type { TFieldValue, TFormErrors, TFormValues } from "../types/form.types";
import type { IFieldConfig } from "../interface/form.interfaces";

export interface IUseBaseFormOptions {
    fields: IFieldConfig[];
    defaultValues?: TFormValues;
    onSubmit: (values: TFormValues) => void | Promise<void>;
    onFieldChange?: (name: string, value: TFieldValue, updateField: (name: string, value: TFieldValue) => void) => void;
}

export interface IUseBaseFormReturn {
    values: TFormValues;
    errors: TFormErrors;
    isSubmitting: boolean;
    handleChange: (name: string, value: TFieldValue) => void;
    handleBlur: (name: string) => void;
    handleSubmit: (e: React.FormEvent) => Promise<void>;
}

export function useBaseForm({
    fields,
    defaultValues,
    onSubmit,
    onFieldChange,
}: IUseBaseFormOptions): IUseBaseFormReturn {
    const [values, setValues] = useState<TFormValues>(() => ({
        ...getDefaultValues(fields),
        ...defaultValues,
    }));
    const [errors, setErrors] = useState<TFormErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    /**
     * On every change: update the value and, if the field already has a visible
     * error, re-validate immediately so the error clears as soon as it's fixed.
     * This avoids the UX anti-pattern where an error lingers after the user
     * has already corrected their input.
     */
    const handleChange = useCallback(
        (name: string, value: TFieldValue) => {
            setValues((prev) => {
                const next = { ...prev, [name]: value };

                setErrors((prevErrors) => {
                    if (!prevErrors[name]) return prevErrors;
                    const field = fields.find((f) => f.name === name)!;
                    const error = validateField(field, value, next);
                    if (!error) {
                        const { [name]: _, ...rest } = prevErrors;
                        return rest;
                    }
                    return { ...prevErrors, [name]: error };
                });

                return next;
            });

            if (onFieldChange) {
                onFieldChange(name, value, (updateName: string, updateValue: TFieldValue) => {
                    setValues((prev) => ({ ...prev, [updateName]: updateValue }));
                });
            }
        },
        [fields, onFieldChange]
    );

    /**
     * On blur: validate only this field so the user sees inline feedback
     * as they move through the form, without triggering a full-form validation.
     */
    const handleBlur = useCallback(
        (name: string) => {
            const field = fields.find((f) => f.name === name)!;
            const error = validateField(field, values[name], values);
            if (error) setErrors((prev) => ({ ...prev, [name]: error }));
        },
        [fields, values]
    );

    /**
     * On submit: validate everything at once. If invalid, surface all errors
     * and move focus to the first offending field for accessibility.
     * If valid, delegate to the consumer's onSubmit and manage loading state.
     */
    const handleSubmit = useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault();
            const newErrors = validateAll(fields, values);

            if (Object.keys(newErrors).length > 0) {
                setErrors(newErrors);
                const firstName = Object.keys(newErrors)[0];
                document.getElementById(firstName)?.focus();
                return;
            }

            setIsSubmitting(true);
            try {
                await onSubmit(values);
            } finally {
                setIsSubmitting(false);
            }
        },
        [fields, values, onSubmit]
    );

    return {
        values,
        errors,
        isSubmitting,
        handleChange,
        handleBlur,
        handleSubmit,
    };
}
